import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../config/supabase';

const SupabaseContext = createContext({});

export const SupabaseProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkUser();
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      if (authListener?.unsubscribe) {
        authListener.unsubscribe();
      }
    };
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }
      setUser(session?.user ?? null);
    } catch (error) {
      console.error('Error checking auth status:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Add connection check
  const checkConnection = async () => {
    try {
      const { data, error } = await supabase
        .from('wallets')
        .select('count')
        .limit(1);

      if (error) throw error;
      console.log('Supabase connection successful');
      return true;
    } catch (error) {
      console.error('Supabase connection error:', error);
      setError('Database connection error: ' + error.message);
      return false;
    }
  };

  const signIn = async (email, password) => {
    if (!email || !password) {
      console.error('Email and password are required for sign in.');
      return;
    }

    const response = await supabase.auth.signInWithPassword({
      email: String(email), // Ensure email is a string
      password: String(password) // Ensure password is a string
    });

    console.log('SignIn Response:', response); // Log the entire response

    const { data, error } = response;

    if (error) {
      console.error('Error signing in:', error.message);
      return error;
    }

    if (data.user) {
      setUser(data.user);
    } else {
      console.error('User data is not available');
    }
  };

  const signUp = async (email, password) => {
    if (!email || !password) {
      console.error('Email and password are required for sign up.');
      return;
    }

    const response = await supabase.auth.signUp({
      email: String(email), // Ensure email is a string
      password: String(password) // Ensure password is a string
    });

    console.log('SignUp Response:', response); // Log the entire response

    const { data, error } = response;

    if (error) {
      console.error('Error signing up:', error.message);
      return error;
    }

    if (data.user) {
      setUser(data.user);
    } else {
      console.error('User data is not available');
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error.message);
      return error;
    }
    setUser(null); // Clear user on sign out
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({ provider: 'google' });
    if (error) {
      console.error('Error signing in with Google:', error.message);
      return error;
    }
  };

  const value = {
    user,
    loading,
    error,
    supabase,
    checkConnection,
    signOut,
    signIn,
    signUp,
    signInWithGoogle,
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <SupabaseContext.Provider value={value}>
      {!loading && children}
    </SupabaseContext.Provider>
  );
};

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
}; 